import { MouseEventHandler, useEffect, useState } from 'react';
import Button from 'react-bootstrap/esm/Button';
import Col from 'react-bootstrap/esm/Col';
import Row from 'react-bootstrap/esm/Row';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../app/store';
import LoadingIndicator from '../loading/LoadingIndicator';
import { listMeasurements } from '../measurements/measurementsSlice';
import { useAuth } from '../signin/auth';
import EditGoal from './EditGoal';
import { deleteGoal, getGoal, selectGoal, writeGoal } from './goalSlice';
import { GoalUpdate } from './types';

type RouteGoalEditParams = {
  goalID: string;
}

const RouteGoalEdit = () => {
  let { goalID } = useParams<keyof RouteGoalEditParams>() as RouteGoalEditParams;

  const auth = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const goal = useSelector((state: RootState) => selectGoal(state));
  const goalStatus = useSelector((state: RootState) => state.goal.status)
  const goalError = useSelector((state: RootState) => state.goal.error)
  const [goalState, setGoalState] = useState<GoalUpdate>({});

  useEffect(() => {
    if (auth.authToken) {
      dispatch(listMeasurements({
        authToken: auth.authToken,
      }));
    }
  }, [dispatch, auth.authToken]);

  useEffect(() => {
    if (auth.authToken) {
      dispatch(getGoal({
        authToken: auth.authToken,
        goalID: goalID,
        includeEntries: false,
      }));
    }
  }, [dispatch, auth.authToken, goalID]);

  useEffect(() => {
    // Redirect to the list view if we can't load a goal for whatever reason.
    if ((goalStatus === 'succeeded' || goalStatus === 'failed') && !goal) {
      navigate("/goals");
    }
  }, [goal, navigate, goalStatus]);

  const onSaveGoalClicked: MouseEventHandler<HTMLButtonElement> = (evt) => {
    if (auth.authToken) {
      dispatch(writeGoal({
        authToken: auth.authToken,
        goal: goalState,
      }));
    } else {
      console.log('No auth token!');
    }
  }

  const onCloneGoalClicked: MouseEventHandler<HTMLButtonElement> = (evt) => {
    navigate(`/goal/${goalID}/clone`)
  }

  const onDeleteGoalClicked: MouseEventHandler<HTMLButtonElement> = async (evt) => {
    if (auth.authToken) {
      await dispatch(deleteGoal({
        authToken: auth.authToken,
        goalID: goalID,
      }));
      navigate("/goals");
    } else {
      console.log('No auth token!');
    }
  }

  return (
    <>
      <Row className="justify-content-end">
        <Col sm="auto">
          <Link to={`/goal/${goalID}`}>
            <Button variant='secondary'>View</Button>
          </Link>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <div>
            {goalStatus === 'loading' && <LoadingIndicator />}
            {goalError && <div>{goalError}</div>}
            {goal &&
              <EditGoal goal={goal} goalState={goalState} setGoalState={setGoalState}>
                <Row>
                  <Col className="col-auto me-auto">
                    <Button className="me-3" variant="primary" onClick={onSaveGoalClicked} disabled={goalStatus === 'loading'}>
                      Save goal
                    </Button>
                    <Button variant="outline-secondary" onClick={onCloneGoalClicked} disabled={goalStatus === 'loading'}>
                      Clone goal
                    </Button>
                  </Col>
                  <Col className="col-auto">
                    <Button variant="danger" onClick={onDeleteGoalClicked} disabled={goalStatus === 'loading'}>
                      Delete goal
                    </Button>
                  </Col>
                </Row>
              </EditGoal>
            }
          </div>
        </Col>
      </Row>
    </>
  );
};

export default RouteGoalEdit;
